import React from 'react';
import DOMPurify from 'dompurify';
import { X, Bot, Trash2 } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import i18n from '../i18n';

export const LegalModal: React.FC<{
  isOpen: boolean;
  type: 'impressum' | 'datenschutz' | 'agb' | 'cookies' | null;
  onClose: () => void;
}> = ({ isOpen, type, onClose }) => {
  const [prsContent, setPrsContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [urContent, setUrContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [arContent, setArContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [ukContent, setUkContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [enContent, setEnContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [trContent, setTrContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [hiContent, setHiContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [faContent, setFaContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [roContent, setRoContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [plContent, setPlContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [ruContent, setRuContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [bgContent, setBgContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [esContent, setEsContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [ptContent, setPtContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  const [psContent, setPsContent] = React.useState<{
    titles: Record<string, string>;
    contents: Record<string, React.ReactNode>;
  } | null>(null);
  // Dynamic content from DB (Admin Panel → Rechtstexte)
  const [dbContent, setDbContent] = React.useState<string | null>(null);
  const [dbLoading, setDbLoading] = React.useState(false);
  const currentLang = i18n.language;

  // Load dynamic content from legal_content table (takes priority over static)
  React.useEffect(() => {
    if (!isOpen || !type) {
      setDbContent(null);
      setDbLoading(false);
      return;
    }
    let cancelled = false;
    setDbLoading(true);
    setDbContent(null);

    // Timeout guard — never show "Laden..." forever
    const timeout = setTimeout(() => {
      if (!cancelled) {
        setDbLoading(false);
      }
    }, 5000);

    (async () => {
      try {
        const { data, error } = await supabase
          .from('legal_content')
          .select('content_html')
          .eq('type', type)
          .eq('language', currentLang)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          console.error('Legal content fetch error:', error.message);
          setDbContent(null);
        } else if (data?.content_html?.trim()) {
          setDbContent(data.content_html);
        } else {
          setDbContent(null);
        }
      } catch (e) {
        console.error('Legal content exception:', e);
        if (!cancelled) setDbContent(null);
      } finally {
        clearTimeout(timeout);
        if (!cancelled) setDbLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isOpen, type, currentLang]);

  // Lazy-load static legal content when needed (fallback)
  React.useEffect(() => {
    if (isOpen && currentLang === 'prs' && !prsContent) {
      import('./LegalContentPrs').then((mod) => {
        setPrsContent({ titles: mod.legalTitlesPrs, contents: mod.legalContentsPrs });
      });
    }
    if (isOpen && currentLang === 'ur' && !urContent) {
      import('./LegalContentUr').then((mod) => {
        setUrContent({ titles: mod.legalTitlesUr, contents: mod.legalContentsUr });
      });
    }
    if (isOpen && currentLang === 'ar' && !arContent) {
      import('./LegalContentAr').then((mod) => {
        setArContent({ titles: mod.legalTitlesAr, contents: mod.legalContentsAr });
      });
    }
    if (isOpen && currentLang === 'uk' && !ukContent) {
      import('./LegalContentUk').then((mod) => {
        setUkContent({ titles: mod.legalTitlesUk, contents: mod.legalContentsUk });
      });
    }
    if (isOpen && currentLang === 'en' && !enContent) {
      import('./LegalContentEn').then((mod) => {
        setEnContent({ titles: mod.legalTitlesEn, contents: mod.legalContentsEn });
      });
    }
    if (isOpen && currentLang === 'tr' && !trContent) {
      import('./LegalContentTr').then((mod) => {
        setTrContent({ titles: mod.legalTitlesTr, contents: mod.legalContentsTr });
      });
    }
    if (isOpen && currentLang === 'hi' && !hiContent) {
      import('./LegalContentHi').then((mod) => {
        setHiContent({ titles: mod.legalTitlesHi, contents: mod.legalContentsHi });
      });
    }
    if (isOpen && currentLang === 'fa' && !faContent) {
      import('./LegalContentFa').then((mod) => {
        setFaContent({ titles: mod.legalTitlesFa, contents: mod.legalContentsFa });
      });
    }
    if (isOpen && currentLang === 'ro' && !roContent) {
      import('./LegalContentRo').then((mod) => {
        setRoContent({ titles: mod.legalTitlesRo, contents: mod.legalContentsRo });
      });
    }
    if (isOpen && currentLang === 'pl' && !plContent) {
      import('./LegalContentPl').then((mod) => {
        setPlContent({ titles: mod.legalTitlesPl, contents: mod.legalContentsPl });
      });
    }
    if (isOpen && currentLang === 'ru' && !ruContent) {
      import('./LegalContentRu').then((mod) => {
        setRuContent({ titles: mod.legalTitlesRu, contents: mod.legalContentsRu });
      });
    }
    if (isOpen && currentLang === 'bg' && !bgContent) {
      import('./LegalContentBg').then((mod) => {
        setBgContent({ titles: mod.legalTitlesBg, contents: mod.legalContentsBg });
      });
    }
    if (isOpen && currentLang === 'es' && !esContent) {
      import('./LegalContentEs').then((mod) => {
        setEsContent({ titles: mod.legalTitlesEs, contents: mod.legalContentsEs });
      });
    }
    if (isOpen && currentLang === 'pt' && !ptContent) {
      import('./LegalContentPt').then((mod) => {
        setPtContent({ titles: mod.legalTitlesPt, contents: mod.legalContentsPt });
      });
    }
    if (isOpen && currentLang === 'ps' && !psContent) {
      import('./LegalContentPs').then((mod) => {
        setPsContent({ titles: mod.legalTitlesPs, contents: mod.legalContentsPs });
      });
    }
  }, [
    isOpen,
    currentLang,
    prsContent,
    urContent,
    arContent,
    ukContent,
    enContent,
    trContent,
    hiContent,
    faContent,
    roContent,
    plContent,
    ruContent,
    bgContent,
    esContent,
    ptContent,
    psContent,
  ]);

  if (!isOpen || !type) return null;

  const titlesDE: Record<string, string> = {
    impressum: 'Impressum',
    datenschutz: 'Datenschutzerklärung',
    agb: 'AGB',
    cookies: 'Cookie-Richtlinie',
  };

  const titles =
    currentLang === 'prs' && prsContent?.titles
      ? prsContent.titles
      : currentLang === 'ps' && psContent?.titles
        ? psContent.titles
        : currentLang === 'ur' && urContent?.titles
          ? urContent.titles
          : currentLang === 'ar' && arContent?.titles
            ? arContent.titles
            : currentLang === 'uk' && ukContent?.titles
              ? ukContent.titles
              : currentLang === 'en' && enContent?.titles
                ? enContent.titles
                : currentLang === 'tr' && trContent?.titles
                  ? trContent.titles
                  : currentLang === 'hi' && hiContent?.titles
                    ? hiContent.titles
                    : currentLang === 'fa' && faContent?.titles
                      ? faContent.titles
                      : currentLang === 'ro' && roContent?.titles
                        ? roContent.titles
                        : currentLang === 'pl' && plContent?.titles
                          ? plContent.titles
                          : currentLang === 'ru' && ruContent?.titles
                            ? ruContent.titles
                            : currentLang === 'bg' && bgContent?.titles
                              ? bgContent.titles
                              : currentLang === 'es' && esContent?.titles
                                ? esContent.titles
                                : currentLang === 'pt' && ptContent?.titles
                                  ? ptContent.titles
                                  : titlesDE;

  const contents: Record<string, React.ReactNode> = {
    impressum: (
      <div className="space-y-3">
        <p>
          <strong>Angaben gemäß § 5 DDG</strong>
        </p>
        <p>
          fahryo.de
          <br />
          Ein Produkt von qhconsulting.de
          <br />
          Inhaber: Qamar Hussain
          <br />
          Bockenheimer Landstraße 17/19
          <br />
          60325 Frankfurt am Main
          <br />
          Deutschland
        </p>

        <p>
          <strong>Kontakt</strong>
          <br />
          Telefon: +49 160 1032895
          <br />
          E-Mail: sales@qhconsulting.de
        </p>

        <p>
          <strong>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG</strong>
          <br />
          DE323301790
        </p>

        <p>
          <strong>Angaben zur Berufshaftpflichtversicherung</strong>
          <br />
          Markel Insurance SE
          <br />
          Sophienstraße 26
          <br />
          80333 München
          <br />
          Räumlicher Geltungsbereich: Deutschland
        </p>

        <hr className="border-gray-200 dark:border-gray-700" />
        <p className="text-xs">
          <strong>Verbraucherstreitbeilegung / Universalschlichtungsstelle</strong>
          <br />
          Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer
          Verbraucherschlichtungsstelle teilzunehmen.
        </p>

        <hr className="border-gray-200 dark:border-gray-700" />
        <p className="text-xs">
          <strong>Haftung für Inhalte</strong>
          <br />
          Als Diensteanbieter sind wir gemäß den allgemeinen Gesetzen für eigene Inhalte auf diesen
          Seiten verantwortlich. Wir sind jedoch nicht verpflichtet, übermittelte oder gespeicherte
          fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine
          rechtswidrige Tätigkeit hinweisen. Eine Haftung ist jedoch erst ab dem Zeitpunkt der
          Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden entfernen wir diese
          Inhalte umgehend.
        </p>

        <hr className="border-gray-200 dark:border-gray-700" />
        <p className="text-xs">
          <strong>Haftung für Links</strong>
          <br />
          Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen
          Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter
          verantwortlich. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links
          umgehend entfernen.
        </p>

        <hr className="border-gray-200 dark:border-gray-700" />
        <p className="text-xs">
          <strong>Urheberrecht</strong>
          <br />
          Die durch die Seitenbetreiber erstellten Inhalte und Werke unterliegen dem deutschen
          Urheberrecht. Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung
          außerhalb der Grenzen des Urheberrechts bedürfen der vorherigen schriftlichen Zustimmung.
          Downloads und Kopien sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.
        </p>

        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-100 dark:border-orange-800 text-xs">
          <p className="font-bold text-primary mb-1 flex items-center gap-2">
            <Bot size={14} /> Hinweis zum KI-Einsatz (EU AI Act)
          </p>
          <p>
            Diese App verwendet ein KI-System (Google Gemini API) als Lernassistenten. Dieses
            KI-System ist gemäß EU-Verordnung 2024/1689 (AI Act) als System mit minimalem Risiko
            klassifiziert. Die KI-generierten Inhalte dienen ausschließlich Lernzwecken und ersetzen
            keine amtliche Prüfungsvorbereitung.
          </p>
        </div>
      </div>
    ),
    datenschutz: (
      <div className="space-y-3">
        <p className="text-xs text-gray-400 italic">Stand: 01.03.2026</p>

        <p>
          <strong>1. Verantwortlicher</strong>
          <br />
          fahryo.de – Ein Produkt von qhconsulting.de
          <br />
          Inhaber Qamar Hussain
          <br />
          Bockenheimer Landstraße 17/19
          <br />
          60325 Frankfurt am Main
          <br />
          E-Mail: sales@qhconsulting.de
          <br />
          Telefon: +49 160 1032895
        </p>

        <p>
          <strong>2. Allgemeine Hinweise zur Datenverarbeitung</strong>
          <br />
          Wir verarbeiten personenbezogene Daten ausschließlich im Rahmen der geltenden gesetzlichen
          Vorschriften, insbesondere der DSGVO und des TDDDG.
        </p>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg text-xs space-y-1">
          <p>• Art. 6 Abs. 1 lit. a DSGVO – Einwilligung</p>
          <p>• Art. 6 Abs. 1 lit. b DSGVO – Vertragserfüllung</p>
          <p>• Art. 6 Abs. 1 lit. c DSGVO – Rechtliche Verpflichtung</p>
          <p>• Art. 6 Abs. 1 lit. f DSGVO – Berechtigtes Interesse</p>
        </div>

        <p>
          <strong>3. Besuch unserer Website</strong>
          <br />
          Beim Aufruf werden technisch erforderliche Informationen verarbeitet (IP-Adresse,
          Datum/Uhrzeit, Browser, Betriebssystem). Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.
        </p>

        <p>
          <strong>4. Hosting & Betrieb der App</strong>
          <br />
          Die Web-App (app.fahryo.de) wird über Google Cloud Run (Region europe-west4, EU)
          betrieben; Datenbank, Authentifizierung und Datei-Speicher laufen über Supabase (Region
          EU/Frankfurt). Unsere Marketing-Website (fahryo.de) wird separat über onepage.io
          bereitgestellt. Rechtsgrundlage: Art. 6 Abs. 1 lit. b/f DSGVO.
        </p>

        <p>
          <strong>5. Kontaktaufnahme</strong>
          <br />
          Bei Kontakt verarbeiten wir Name, E-Mail, ggf. Telefonnummer und Nachricht.
          Rechtsgrundlage: Art. 6 Abs. 1 lit. b/f DSGVO.
        </p>

        <p>
          <strong>6. Kontakt über WhatsApp</strong>
          <br />
          Bei Kontakt über WhatsApp verarbeiten wir Mobilfunknummer, Name und Nachricht. WhatsApp
          verarbeitet Daten auch eigenverantwortlich. Nutzung erfolgt freiwillig. Rechtsgrundlage:
          Art. 6 Abs. 1 lit. b/f DSGVO.
        </p>

        <p>
          <strong>7. Registrierung & App-Nutzung</strong>
          <br />
          Bei Kontoerstellung verarbeiten wir: Name/Benutzername, E-Mail, Anmeldedaten,
          Kontoeinstellungen, Nutzungsdaten, Supportanfragen. Rechtsgrundlage: Art. 6 Abs. 1 lit. b
          DSGVO.
        </p>

        <p>
          <strong>8. Lernfortschritt & personalisierte Inhalte</strong>
          <br />
          Zur Bereitstellung der Lernfunktionen: bearbeitete Fragen, richtige/falsche Antworten,
          Kapitelstände, Lernhistorie, Testergebnisse, Lernempfehlungen. Rechtsgrundlage: Art. 6
          Abs. 1 lit. b DSGVO.
        </p>

        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-100 dark:border-orange-800">
          <p className="font-bold text-primary mb-1 flex items-center gap-2">
            <Bot size={14} /> 9. KI-Funktionen (Google Gemini API)
          </p>
          <p className="text-xs">
            Für KI-gestützte Funktionen nutzen wir die Google Gemini API über Google Cloud.
            Verarbeitete Daten: Texteingaben, Prompts, KI-Antworten, technische Metadaten,
            kontextbezogene Eingaben zur Lernunterstützung.
          </p>
          <p className="text-xs mt-2">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.</p>
          <p className="text-xs mt-2 font-semibold text-red-600">
            Bitte geben Sie keine sensiblen personenbezogenen Daten in freie Eingabefelder ein.
          </p>
          <div className="mt-3 p-2 bg-white dark:bg-gray-900 rounded border border-orange-200 dark:border-orange-700">
            <p className="text-xs font-bold text-primary mb-1">
              EU AI Act Transparenz (Art. 50, VO 2024/1689):
            </p>
            <ul className="text-xs list-disc pl-4 space-y-1">
              <li>
                <strong>KI-System:</strong> Google Gemini 2.5 Flash (Large Language Model)
              </li>
              <li>
                <strong>Zweck:</strong> Lernunterstützung für die theoretische Führerscheinprüfung
              </li>
              <li>
                <strong>Risiko-Klasse:</strong> Minimales Risiko (kein Hochrisiko-System)
              </li>
              <li>
                <strong>Einschränkungen:</strong> KI kann Fehler machen. Antworten ersetzen keine
                amtliche Beratung
              </li>
              <li>
                <strong>Opt-Out:</strong> KI-Personalisierung in Profileinstellungen deaktivierbar
              </li>
            </ul>
          </div>
        </div>

        <p>
          <strong>10. Login über Google</strong>
          <br />
          Wir bieten die Anmeldung über Google Sign-In an. Wir erhalten: Name, E-Mail, eindeutige
          Nutzerkennung. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO. (Eine Anmeldung über „Sign in
          with Apple" ist geplant, aber derzeit nicht aktiv.)
        </p>

        <p>
          <strong>11. Zahlungsabwicklung (CopeCart)</strong>
          <br />
          Kostenpflichtige Leistungen werden über unseren Zahlungsdienstleister CopeCart
          abgewickelt. Im CopeCart-Checkout stehen ggf. Zahlungsmethoden wie PayPal oder Apple Pay
          zur Verfügung; die Zahlungsdaten verarbeitet dabei der jeweilige Anbieter
          eigenverantwortlich. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
        </p>

        <p>
          <strong>12. Google Analytics</strong>
          <br />
          Nur mit Ihrer ausdrücklichen Einwilligung. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO
          i.V.m. § 25 Abs. 1 TDDDG. Widerruf jederzeit über Consent-Einstellungen möglich.
        </p>

        <p>
          <strong>13. Google Fonts</strong>
          <br />
          Google Fonts werden nach Möglichkeit lokal gehostet. Rechtsgrundlage: Art. 6 Abs. 1 lit. f
          DSGVO (Self-Hosting) oder Art. 6 Abs. 1 lit. a DSGVO (bei Einwilligung).
        </p>

        <p>
          <strong>13a. Fehleranalyse, Monitoring & Sicherheit</strong>
          <br />
          <strong>Sentry:</strong> Zur Stabilitäts- und Fehleranalyse setzen wir Sentry (EU-Hosting,
          de.sentry.io) ein. Die reine Fehlererfassung (Fehlerdaten, technische
          Geräte-/Browserinfos, pseudonyme Nutzer-ID) erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f
          DSGVO. Session Replay (maskierte Bildschirmaufzeichnung im Fehlerfall) und
          Performance-Messung erfolgen nur mit Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO i.V.m.
          § 25 TDDDG). Texteingaben werden maskiert. <br />
          <strong>Verfügbarkeits-Monitoring (BetterStack):</strong> Verarbeitung von IP-Adresse und
          Zeitstempel zur Überwachung der Erreichbarkeit, Art. 6 Abs. 1 lit. f DSGVO. <br />
          <strong>Push-Benachrichtigungen:</strong> Mit Ihrer Einwilligung speichern wir einen
          Push-Endpunkt und kryptografische Schlüssel für Lern-Erinnerungen, Art. 6 Abs. 1 lit. a
          DSGVO; Widerruf jederzeit in den Einstellungen. <br />
          <strong>Passwort-Sicherheitsprüfung (Have I Been Pwned):</strong> Bei Registrierung/
          Passwortänderung wird ausschließlich ein 5-stelliges Präfix des SHA-1-Hashwerts Ihres
          Passworts an haveibeenpwned.com (k-Anonymität) übermittelt; Ihr Passwort verlässt Ihr
          Gerät niemals vollständig. Art. 6 Abs. 1 lit. f DSGVO / Art. 32 DSGVO.
        </p>

        <p>
          <strong>14. Cookies & Consent-Management</strong>
          <br />
          Technisch erforderliche Cookies/Speichertechnologien werden ohne Einwilligung gesetzt. Für
          Analyse- und Tracking-Technologien (Google Analytics, Sentry Session Replay/Performance)
          wird vorab Ihre Einwilligung über unser eigenes Consent-Banner eingeholt. Ihre
          Entscheidung wird lokal sowie serverseitig (zu Nachweiszwecken) gespeichert; Widerruf
          jederzeit möglich.
        </p>

        <p>
          <strong>15. Empfänger personenbezogener Daten</strong>
        </p>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded text-xs space-y-0.5">
          <p>
            • Google Cloud (Hosting/Gemini API) • Supabase (Backend/DB/Auth) • Google Analytics •
            Google Sign-In • CopeCart (Zahlung) • Resend (E-Mail-Versand) • Sentry (Fehleranalyse) •
            BetterStack (Monitoring) • Have I Been Pwned (Passwortprüfung) • WhatsApp (auf Wunsch) •
            onepage.io (nur Marketing-Website)
          </p>
        </div>

        <p>
          <strong>16. Supabase (Backend/Datenbank/Auth)</strong>
          <br />
          Für Backend, Datenbank, Speicherung und Authentifizierung nutzen wir Supabase.
          Rechtsgrundlage: Art. 6 Abs. 1 lit. b/f DSGVO.
        </p>

        <p>
          <strong>17. Drittlandübermittlungen</strong>
          <br />
          Übermittlungen in Drittländer erfolgen auf Grundlage von Angemessenheitsbeschlüssen,
          Standardvertragsklauseln oder sonstigen geeigneten Garantien.
        </p>

        <p>
          <strong>18. Speicherdauer</strong>
        </p>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg text-xs space-y-2">
          <div className="grid grid-cols-[1fr,auto] gap-x-3 gap-y-1.5">
            <span className="font-semibold">Dienst / Datenart</span>
            <span className="font-semibold text-right">Speicherdauer</span>

            <span>Profil, Lernfortschritt, KI-Chats (Supabase)</span>
            <span className="text-right">Bis zur Kontolöschung</span>

            <span>Avatare, Uploads (Supabase Storage)</span>
            <span className="text-right">Bis zur Kontolöschung</span>

            <span>Google Gemini API (Prompts/Antworten)</span>
            <span className="text-right">30 Tage¹</span>

            <span>E-Mail-Versand (Resend)</span>
            <span className="text-right">30 Tage</span>

            <span>Fehlerberichte (Sentry)</span>
            <span className="text-right">90 Tage</span>

            <span>Google Analytics (bei Einwilligung)</span>
            <span className="text-right">26 Monate</span>

            <span>Kaufdaten (gesetzl. Aufbewahrung)</span>
            <span className="text-right">10 Jahre²</span>
          </div>
          <hr className="border-gray-200 dark:border-gray-700" />
          <p className="text-gray-500">
            ¹ Google speichert API-Anfragen 30 Tage für Missbrauchsüberwachung, nutzt sie aber{' '}
            <strong>nicht</strong> für Modelltraining (bezahlte API).
          </p>
          <p className="text-gray-500">
            ² Gemäß § 257 HGB, § 147 AO (steuerliche Aufbewahrungspflicht).
          </p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-100 dark:border-green-800 text-xs mt-2">
          <p className="font-bold text-green-700 dark:text-green-400 mb-1 flex items-center gap-1">
            <Trash2 size={13} /> Bei Kontolöschung (Profil → „Alle Daten löschen"):
          </p>
          <p>
            Alle personenbezogenen Daten werden <strong>sofort und unwiderruflich</strong> gelöscht:
            Profildaten, KI-Coach-Gespräche, Lernfortschritt, Dateien im Speicher und Ihr
            Benutzerkonto. Daten bei Drittanbietern (Gemini, Sentry, Resend) werden nach ihrer
            jeweiligen Haltefrist automatisch gelöscht.
          </p>
        </div>

        <p>
          <strong>19. Datensicherheit</strong>
          <br />
          Wir treffen angemessene technische und organisatorische Maßnahmen zum Schutz
          personenbezogener Daten.
        </p>

        <p>
          <strong>20. Ihre Rechte</strong>
        </p>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg text-xs space-y-1">
          <p>
            • <strong>Auskunft</strong> (Art. 15 DSGVO)
          </p>
          <p>
            • <strong>Berichtigung</strong> (Art. 16 DSGVO)
          </p>
          <p>
            • <strong>Löschung</strong> (Art. 17 DSGVO) – In-App: Profil → „Alle Daten löschen"
          </p>
          <p>
            • <strong>Einschränkung</strong> (Art. 18 DSGVO)
          </p>
          <p>
            • <strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO) – In-App: Profil → „Daten
            exportieren"
          </p>
          <p>
            • <strong>Widerspruch</strong> (Art. 21 DSGVO)
          </p>
          <p>
            • <strong>Widerruf</strong> erteilter Einwilligungen
          </p>
          <p>
            • <strong>Beschwerde</strong> bei einer Datenschutzaufsichtsbehörde
          </p>
        </div>

        <p>
          <strong>21. Beschwerderecht</strong>
          <br />
          Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren. Zuständig:
          Der Hessische Beauftragte für Datenschutz und Informationsfreiheit.
        </p>

        <p>
          <strong>22. Minderjährige</strong>
          <br />
          Da sich unser Angebot auch an Fahrschüler richten kann, können bei Minderjährigen
          besondere gesetzliche Anforderungen gelten. Soweit eine Einwilligung erforderlich ist und
          das gesetzlich relevante Alter nicht erreicht ist, ist die Einwilligung der
          Erziehungsberechtigten erforderlich.
        </p>

        <p>
          <strong>23. Änderungen dieser Datenschutzerklärung</strong>
          <br />
          Wir behalten uns vor, diese Datenschutzerklärung anzupassen, wenn sich technische,
          rechtliche oder organisatorische Änderungen ergeben.
        </p>
      </div>
    ),
    agb: (
      <div className="space-y-3">
        <p>
          <strong>§ 1 Geltungsbereich</strong>
          <br />
          Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Fahryo App und Webseite
          (fahryo.de), betrieben von QH Consulting, Inhaber Qamar Hussain, Bockenheimer Landstraße
          17/19, 60325 Frankfurt am Main (nachfolgend „Anbieter").
        </p>

        <p>
          <strong>§ 2 Leistungsbeschreibung</strong>
          <br />
          Fahryo bietet eine digitale Lernplattform zur Vorbereitung auf die theoretische
          Führerscheinprüfung. Die App stellt Übungsfragen, Prüfungssimulationen und einen
          KI-gestützten Lernassistenten bereit. Die Inhalte ersetzen <strong>nicht</strong> den
          Unterricht in der Fahrschule und sind <strong>keine amtlichen Prüfungsfragen</strong>.
        </p>

        <p>
          <strong>§ 3 KI-Haftungsausschluss</strong>
        </p>
        <div className="bg-red-50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-800 text-xs">
          <p>
            Der KI-Coach („Fahryo Coach") basiert auf einem KI-System (Google Gemini).{' '}
            <strong>
              Der Anbieter übernimmt keine Gewähr für die Richtigkeit, Vollständigkeit oder
              Aktualität der KI-generierten Antworten.
            </strong>{' '}
            KI-Systeme können fehlerhafte oder halluzinierte Inhalte erzeugen. Die Nutzung erfolgt
            auf eigenes Risiko. Bei Widersprüchen zwischen KI-Antworten und der StVO/FeV gilt stets
            das Gesetz.
          </p>
        </div>

        <p>
          <strong>§ 4 Premium-Mitgliedschaft</strong>
          <br />
          Die Bezahlung der Premium-Version erfolgt über CopeCart GmbH. Der Kaufpreis und die
          Vertragslaufzeit ergeben sich aus der jeweiligen Angebotsseite. Das Widerrufsrecht beträgt
          14 Tage ab Kauf gemäß § 356 BGB.
        </p>

        <p>
          <strong>§ 5 Datenschutz & KI</strong>
          <br />
          Details zur Datenverarbeitung, insbesondere zur Nutzung der Google Gemini API und der
          KI-Personalisierung, finden Sie in unserer Datenschutzerklärung.
        </p>

        <p>
          <strong>§ 6 Haftungsbeschränkung</strong>
          <br />
          Der Anbieter haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit. Bei leichter
          Fahrlässigkeit haftet der Anbieter nur bei Verletzung wesentlicher Vertragspflichten,
          begrenzt auf den vorhersehbaren, vertragstypischen Schaden. Die Haftung für Datenverlust
          wird auf den typischen Wiederherstellungsaufwand begrenzt.
        </p>

        <p>
          <strong>§ 7 Verfügbarkeit</strong>
          <br />
          Der Anbieter gewährleistet eine Verfügbarkeit der App von 95% im Jahresmittel.
          Wartungsarbeiten sind hiervon ausgenommen.
        </p>

        <p>
          <strong>§ 8 Schlussbestimmungen</strong>
          <br />
          Es gilt das Recht der Bundesrepublik Deutschland. Sollten einzelne Bestimmungen unwirksam
          sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
        </p>

        <p className="text-xs text-gray-400">Stand: März 2026</p>
      </div>
    ),
    cookies: (
      <div className="space-y-3">
        <p className="text-xs text-gray-400 italic">Stand: 01.03.2026</p>

        <p>
          <strong>1. Allgemeine Hinweise</strong>
          <br />
          Auf unserer Website fahryo.de verwenden wir Cookies und ähnliche Technologien. Einige sind
          technisch erforderlich, andere nur mit Ihrer Einwilligung.
        </p>

        <p>
          <strong>2. Rechtsgrundlagen</strong>
        </p>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded text-xs space-y-1">
          <p>• Technisch erforderlich: § 25 Abs. 2 TDDDG i.V.m. Art. 6 Abs. 1 lit. f DSGVO</p>
          <p>• Nicht erforderlich (Analyse): § 25 Abs. 1 TDDDG i.V.m. Art. 6 Abs. 1 lit. a DSGVO</p>
        </div>

        <p>
          <strong>3. Consent-Management</strong>
          <br />
          Für die Verwaltung Ihrer Einwilligungen nutzen wir das Consent-Tool von onepage.io. Ihre
          Auswahl können Sie jederzeit ändern oder widerrufen.
        </p>

        <p>
          <strong>4. Arten der eingesetzten Cookies</strong>
        </p>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg text-xs space-y-2">
          <p>
            <strong>a) Technisch erforderlich:</strong> Consent-Speicherung, Sicherheits- und
            Sitzungsfunktionen, Stabilität, Missbrauchsschutz. Können nicht abgewählt werden.
          </p>
          <p>
            <strong>b) Analyse-/Statistik-Cookies:</strong> Nur mit Einwilligung. Google Analytics
            (GA4) – First-Party-Cookie _ga. Tracking erfolgt nicht, wenn Analytics Storage über
            Consent Mode deaktiviert ist.
          </p>
        </div>

        <p>
          <strong>5. Eingesetzte Dienste</strong>
        </p>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded text-xs space-y-1">
          <p>
            • <strong>onepage.io</strong> – Cookie-Banner und Consent-Verwaltung
          </p>
          <p>
            • <strong>Google Analytics 4</strong> – Nur bei Einwilligung, First-Party-Cookies
          </p>
          <p>
            • <strong>Supabase</strong> – Auth-Informationen in LocalStorage oder sicheren Cookies
          </p>
        </div>

        <p>
          <strong>6. Login- und Sitzungsfunktionen</strong>
          <br />
          Cookies für Authentifizierung und Sicherheit sind technisch erforderlich. Supabase
          speichert Auth-Informationen je nach Implementierung in LocalStorage oder sicheren
          Cookies.
        </p>

        <p>
          <strong>7. Speicherdauer</strong>
          <br />
          Session-Cookies werden nach Browser-Sitzung gelöscht. Persistente Cookies bleiben bis zum
          Ablauf oder manueller Löschung gespeichert.
        </p>

        <p>
          <strong>8. Widerruf und Änderung</strong>
          <br />
          Widerruf jederzeit über Cookie-Banner oder Browser-Einstellungen möglich. Bereits gesetzte
          Cookies können über den Browser gelöscht werden.
        </p>

        <p>
          <strong>9. Browser-Einstellungen</strong>
          <br />
          Die meisten Browser ermöglichen das Anzeigen, Löschen und Blockieren von Cookies. Bei
          vollständiger Deaktivierung kann die Funktionalität eingeschränkt sein.
        </p>

        <p>
          <strong>10. Weitere Informationen</strong>
          <br />
          Weitere Informationen zur Verarbeitung finden Sie in unserer Datenschutzerklärung.
        </p>
      </div>
    ),
  };

  // Sprach-Content via Map statt langer Ternary-Kette
  const langContentMap: Record<string, any> = {
    prs: prsContent,
    ps: psContent,
    ur: urContent,
    ar: arContent,
    uk: ukContent,
    en: enContent,
    tr: trContent,
    hi: hiContent,
    fa: faContent,
    ro: roContent,
    pl: plContent,
    ru: ruContent,
    bg: bgContent,
    es: esContent,
    pt: ptContent,
  };
  const localizedContent = langContentMap[currentLang]?.contents?.[type] ?? contents[type];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
    >
      <div className="glass-strong rounded-2xl max-w-sm w-full max-h-[80vh] flex flex-col">
        <div className="p-5 border-b border-[var(--f-border)] flex justify-between items-center">
          <h2 id="legal-modal-title" className="text-xl font-bold text-gray-900 dark:text-white">
            {titles[type]}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          {dbLoading ? (
            <div style={{ textAlign: 'center', padding: 20, opacity: 0.4 }}>Laden...</div>
          ) : dbContent ? (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(dbContent) }} />
          ) : (
            localizedContent
          )}
        </div>
      </div>
    </div>
  );
};
